'use client';
import React, { useState } from 'react';

const FORMATIONS = [
  {
    id: 'triangle',
    name: 'Triangle',
    points: 1,
    pointsLabel: '+1 point',
    color: '#F59E0B',
    bgColor: 'rgba(245,158,11,0.08)',
    borderColor: 'rgba(245,158,11,0.3)',
    description:
      'Three of your pieces forming a triangular pattern — any three mutually adjacent positions that are not collinear.',
    svgPath: 'triangle',
    positions: ['A', 'B', 'C'],
    connections: [['A','B'],['B','C'],['A','C']],
  },
  {
    id: 'line',
    name: 'Line',
    points: 3,
    pointsLabel: '+3 points',
    color: '#06B6D4',
    bgColor: 'rgba(6,182,212,0.08)',
    borderColor: 'rgba(6,182,212,0.3)',
    description:
      'Three of your pieces in a straight row, column, or diagonal within either the left or right grid.',
    svgPath: 'line',
    positions: ['A', 'B', 'C'],
    connections: [['A','B'],['B','C']],
  },
  {
    id: 'square',
    name: 'Square',
    points: 2,
    pointsLabel: '+2 points',
    color: '#10B981',
    bgColor: 'rgba(16,185,129,0.08)',
    borderColor: 'rgba(16,185,129,0.3)',
    description:
      'Four of your pieces forming a 2×2 square within a grid — any of the four possible 2×2 sub-grids.',
    svgPath: 'square',
    positions: ['A', 'B', 'C', 'D'],
    connections: [['A','B'],['C','D'],['A','C'],['B','D'],['A','D'],['B','C']],
  },
];

type FormationSVGProps = {
  type: string;
  color: string;
  hovered: boolean;
};

function FormationSVG({ type, color, hovered }: FormationSVGProps) {
  if (type === 'triangle') {
    return (
      <svg viewBox="0 0 60 60" className="w-full h-full">
        <line x1="15" y1="45" x2="30" y2="15" stroke={color} strokeWidth="1.5" opacity="0.6" />
        <line x1="30" y1="15" x2="45" y2="45" stroke={color} strokeWidth="1.5" opacity="0.6" />
        <line x1="15" y1="45" x2="45" y2="45" stroke={color} strokeWidth="1.5" opacity="0.6" />
        {hovered && (
          <polygon points="15,45 30,15 45,45" fill={color} opacity="0.12" />
        )}
        {[{ cx: 15, cy: 45 }, { cx: 30, cy: 15 }, { cx: 45, cy: 45 }].map((pt, i) => (
          <circle
            key={`tri-pt-${i}`}
            cx={pt.cx}
            cy={pt.cy}
            r={hovered ? 6 : 5}
            fill={color}
            style={{ filter: `drop-shadow(0 0 4px ${color})`, transition: 'r 0.2s' }}
          />
        ))}
      </svg>
    );
  }
  if (type === 'line') {
    return (
      <svg viewBox="0 0 80 40" className="w-full h-full">
        <line x1="10" y1="20" x2="70" y2="20" stroke={color} strokeWidth="1.5" opacity="0.6" />
        {[10, 40, 70].map((cx, i) => (
          <circle
            key={`line-pt-${i}`}
            cx={cx}
            cy={20}
            r={hovered ? 7 : 6}
            fill={color}
            style={{ filter: `drop-shadow(0 0 4px ${color})`, transition: 'r 0.2s' }}
          />
        ))}
      </svg>
    );
  }
  // square
  return (
    <svg viewBox="0 0 60 60" className="w-full h-full">
      <line x1="15" y1="15" x2="45" y2="15" stroke={color} strokeWidth="1.5" opacity="0.6" />
      <line x1="45" y1="15" x2="45" y2="45" stroke={color} strokeWidth="1.5" opacity="0.6" />
      <line x1="45" y1="45" x2="15" y2="45" stroke={color} strokeWidth="1.5" opacity="0.6" />
      <line x1="15" y1="45" x2="15" y2="15" stroke={color} strokeWidth="1.5" opacity="0.6" />
      {hovered && (
        <rect x="15" y="15" width="30" height="30" fill={color} opacity="0.1" />
      )}
      {[{ cx: 15, cy: 15 }, { cx: 45, cy: 15 }, { cx: 45, cy: 45 }, { cx: 15, cy: 45 }].map((pt, i) => (
        <circle
          key={`sq-pt-${i}`}
          cx={pt.cx}
          cy={pt.cy}
          r={hovered ? 7 : 6}
          fill={color}
          style={{ filter: `drop-shadow(0 0 4px ${color})`, transition: 'r 0.2s' }}
        />
      ))}
    </svg>
  );
}

export default function FormationCards() {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <section id="formations" className="py-24 px-6 lg:px-12 max-w-screen-2xl mx-auto">
      <div className="text-center mb-16">
        <h2 className="text-hero-md text-foreground mb-4">Master the Formations</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          ViNCi rewards strategic thinking. Every piece you place can create multiple formations simultaneously.
          Score more by planning ahead.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {FORMATIONS.map((formation) => {
          const isHovered = hoveredId === formation.id;
          return (
            <div
              key={`formation-card-${formation.id}`}
              className="relative rounded-2xl p-6 border cursor-pointer transition-all duration-300"
              style={{
                background: isHovered ? formation.bgColor : 'rgba(22,21,37,0.8)',
                borderColor: isHovered ? formation.color : 'var(--border)',
                transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
                boxShadow: isHovered
                  ? `0 8px 32px ${formation.color}30, 0 0 0 1px ${formation.color}40`
                  : 'none',
              }}
              onMouseEnter={() => setHoveredId(formation.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* Points badge */}
              <div
                className="absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-700 font-mono-game"
                style={{
                  background: `${formation.color}20`,
                  color: formation.color,
                  border: `1px solid ${formation.color}40`,
                }}
              >
                {formation.pointsLabel}
              </div>

              {/* SVG diagram */}
              <div className="w-full h-24 mb-5 flex items-center justify-center">
                <div className="w-24 h-16">
                  <FormationSVG type={formation.id} color={formation.color} hovered={isHovered} />
                </div>
              </div>

              <h3 className="text-xl font-700 text-foreground mb-2">{formation.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {formation.description}
              </p>

              {/* Stack hint */}
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  <span style={{ color: formation.color }} className="font-600">Pro tip: </span>
                  {formation.id === 'triangle' && 'Multiple triangles can share pieces — one move can score 2+ triangles.'}
                  {formation.id === 'line' && 'The highest-value formation. A single row scores 3 points instantly.'}
                  {formation.id === 'square' && 'Squares contain 4 triangles inside — placing the 4th piece can score both.'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}